import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  getPracticeInfo,
  PracticeInfo,
  KioskDueList,
  KioskV2PackageOption,
  KioskV2Session,
  endKioskSession,
  issueKioskObligation,
  issueKioskPackage,
  kioskCheckIn,
  kioskDue,
  normalizeDueList,
} from '../api/bookingApi';
import {
  DUE_REFRESH_FAILED,
  dueSection,
  isObligationNotDue,
} from '../components/form-package-v2/kioskDueView';
import { saveKioskReturn } from '../components/form-package-v2/kioskReturn';
import { useKioskV2Session } from '../components/form-package-v2/KioskV2SessionContext';
import Shell from '../components/Shell';
import SigningSuccess from '../components/signing/SigningSuccess';
import LoadingState from '../components/LoadingState';
import BirthdayField from '../components/custom-form/BirthdayField';
import { parseBirthday, type BirthdayParts } from '../components/custom-form/birthday';
import { getLocalToday } from '../utils/date';

type State = 'identify' | 'hub' | 'done';

export default function KioskPage() {
  const { slug } = useParams<{ slug?: string }>();
  const clientId = slug || '';
  const navigate = useNavigate();
  const retainedKiosk = useKioskV2Session();
  const retainedSession = retainedKiosk.retained?.clientId === clientId
    ? retainedKiosk.retained.session
    : null;

  const [practiceInfo, setPracticeInfo] = useState<PracticeInfo | null>(null);
  const [practiceLoading, setPracticeLoading] = useState(false);
  const [state, setState] = useState<State>(retainedSession ? 'hub' : 'identify');
  const [session, setSession] = useState<KioskV2Session | null>(retainedSession);
  const [doneFormsSubmitted, setDoneFormsSubmitted] = useState(false);
  // The key being opened — a menu `package_key` or a due `decision_key`; both lanes
  // share one busy state so a tablet cannot open two forms at once.
  const [issuingPackageKey, setIssuingPackageKey] = useState<string | null>(null);
  const [packageError, setPackageError] = useState('');
  // operaitor/backend#2619 — what the policy says is owed. `null` means "not read
  // yet": a fresh check-in fills it from the session; a hub mounted from a retained
  // session (the patient came back from a form) re-reads it so a completed form
  // drops off the screen.
  const [due, setDue] = useState<KioskDueList | null>(null);
  const [dueNotice, setDueNotice] = useState('');

  useEffect(() => {
    if (!clientId) return;
    setPracticeLoading(true);
    getPracticeInfo(clientId)
      .then(setPracticeInfo)
      .catch(() => {})
      .finally(() => setPracticeLoading(false));
  }, [clientId]);

  useEffect(() => {
    if (!clientId) return;
    // One-time PHI cleanup. The V1 kiosk lane's SigningFormRenderer (deleted with
    // operaitor/backend#1604 slice 2) autosaved agreement drafts — patient name,
    // typed signature fields — to localStorage under this key on shared tablets,
    // and nothing ever read them back. Remove the key so those drafts do not
    // outlive the renderer that wrote them. Best-effort: storage may be blocked.
    try {
      localStorage.removeItem(`operaitor-signing-draft:${clientId}`);
    } catch {
      /* storage unavailable — nothing to clean */
    }
  }, [clientId]);

  useEffect(() => {
    if (state !== 'hub' || !session || due !== null) return;
    let cancelled = false;
    kioskDue(clientId, session.kiosk_token)
      .then((list) => { if (!cancelled) setDue(list); })
      // A failed re-read shows the menu, or front-desk guidance if it is empty.
      .catch(() => { if (!cancelled) setDue(DUE_REFRESH_FAILED); });
    return () => { cancelled = true; };
  }, [state, session, due, clientId]);

  function handleSessionReceived(s: KioskV2Session) {
    setDoneFormsSubmitted(false);
    setSession(s);
    setDue(normalizeDueList(s));
    setDueNotice('');
    retainedKiosk.retain(clientId, s);
    setState('hub');
  }

  function handleReset() {
    setDoneFormsSubmitted(false);
    setState('identify');
    setSession(null);
    setDue(null);
    setDueNotice('');
    retainedKiosk.clear();
    setPackageError('');
  }

  function handleV2Done() {
    setDoneFormsSubmitted(
      retainedKiosk.retained?.clientId === clientId
      && retainedKiosk.retained.session.kiosk_token === session?.kiosk_token
      && retainedKiosk.retained.hasSubmittedForms
    );
    if (session) {
      // Revoke server-side at the EARLIEST end-of-session signal — the success
      // screen can sit for minutes on a shared tablet, and the bearer must stop
      // issuing the moment the patient says they're done. Fire-and-forget: the
      // endpoint is idempotent and best-effort; on failure, token expiry is the
      // backstop, so there is nothing useful to show the patient.
      endKioskSession(clientId, session.kiosk_token).catch(() => {});
      // Also drop the retained bearer so a remount of /kiosk/{clientId} lands
      // on identify instead of resuming a hub whose token is now revoked.
      retainedKiosk.clear();
    }
    setState('done');
  }

  function openIssuedForm(issued: { package_instance_id: string; form_url: string; expires_at: string }) {
    saveKioskReturn(
      issued.package_instance_id,
      `/kiosk/${clientId}`,
      issued.expires_at
    );
    const target = new URL(issued.form_url, window.location.origin);
    if (target.origin !== window.location.origin) {
      throw new Error('Unexpected form host');
    }
    navigate(`${target.pathname}${target.search}${target.hash}`);
  }

  async function handleV2PackageSelect(packageKey: string) {
    if (!session || issuingPackageKey) return;
    setPackageError('');
    setDueNotice('');
    setIssuingPackageKey(packageKey);
    try {
      openIssuedForm(await issueKioskPackage(clientId, session.kiosk_token, packageKey));
    } catch {
      setPackageError("We couldn't open that form. Please ask the front desk for help.");
      setIssuingPackageKey(null);
    }
  }

  async function handleV2DueSelect(decisionKey: string) {
    if (!session || issuingPackageKey) return;
    setPackageError('');
    setDueNotice('');
    setIssuingPackageKey(decisionKey);
    try {
      // Resumes the link the SMS chase already sent when one is live; the server
      // re-decides before honouring the key.
      openIssuedForm(await issueKioskObligation(clientId, session.kiosk_token, decisionKey));
    } catch (err: unknown) {
      if (isObligationNotDue(err)) {
        setDueNotice('That form is no longer due. Your list has been refreshed.');
        setDue(null); // triggers the re-read above
      } else {
        setPackageError("We couldn't open that form. Please ask the front desk for help.");
      }
      setIssuingPackageKey(null);
    }
  }

  if (practiceLoading) {
    return (
      <Shell practiceInfo={practiceInfo}>
        <LoadingState message="Loading practice..." />
      </Shell>
    );
  }

  if (practiceInfo && !practiceInfo.kiosk_enabled) {
    return (
      <Shell practiceInfo={practiceInfo}>
        <div className="text-center py-12">
          <h2 className="text-2xl font-bold text-gray-900">Kiosk Unavailable</h2>
          <p className="text-gray-500 mt-2">Kiosk check-in is not available for this practice.</p>
        </div>
      </Shell>
    );
  }

  return (
    <Shell practiceInfo={practiceInfo}>
      <AnimatePresence mode="wait">
        <motion.div
          key={state}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
        >
          {state === 'identify' && (
            <KioskIdentifyPanel clientId={clientId} onSession={handleSessionReceived} />
          )}
          {state === 'hub' && session && (
            <V2FormHubPanel
              patientName={session.patient_name}
              packages={session.packages}
              due={due}
              dueNotice={dueNotice}
              issuingPackageKey={issuingPackageKey}
              error={packageError}
              onSelect={handleV2PackageSelect}
              onSelectDue={handleV2DueSelect}
              onDone={handleV2Done}
            />
          )}
          {state === 'done' && (
            <SigningSuccess
              formsSubmitted={doneFormsSubmitted}
              patientName={session?.patient_name ?? ''}
              onReset={handleReset}
              resetLabel="Done"
            />
          )}
        </motion.div>
      </AnimatePresence>
    </Shell>
  );
}

function V2FormHubPanel({
  patientName,
  packages,
  due,
  dueNotice,
  issuingPackageKey,
  error,
  onSelect,
  onSelectDue,
  onDone,
}: {
  patientName?: string | null;
  packages: KioskV2PackageOption[];
  due: KioskDueList | null;
  dueNotice: string;
  issuingPackageKey: string | null;
  error: string;
  onSelect: (packageKey: string) => void;
  onSelectDue: (decisionKey: string) => void;
  onDone: () => void;
}) {
  const section = dueSection(due, packages.length > 0);
  // With a due section on screen the menu is "other forms"; without one it is the
  // whole hub, worded as before #2619.
  const menuIsSecondary = section.kind !== 'hidden';
  return (
    <div>
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-900">
          {patientName ? `Welcome, ${patientName}` : 'Welcome'}
        </h2>
        {(section.kind === 'hidden' || section.kind === 'list' || section.kind === 'loading') && (
          <p className="text-gray-500 mt-2">
            {menuIsSecondary
              ? 'Please complete the forms below.'
              : 'Choose the form the front desk asked you to complete.'}
          </p>
        )}
      </div>
      <div className="space-y-3 max-w-lg mx-auto">
        {section.kind === 'loading' && (
          <p className="text-sm text-gray-500 text-center">Checking which forms you need…</p>
        )}
        {section.kind === 'list' && (
          <>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              Forms to complete
            </h3>
            {section.items.map((item) => (
              <FormHubRow
                key={item.decision_key}
                label={item.name}
                description={item.description || 'Please complete this form'}
                loading={issuingPackageKey === item.decision_key}
                disabled={issuingPackageKey !== null}
                onSelect={() => onSelectDue(item.decision_key)}
              />
            ))}
          </>
        )}
        {section.kind === 'none' && (
          <p className="text-center text-gray-700">No forms outstanding — check-in complete</p>
        )}
        {section.kind === 'unavailable' && (
          <p className="text-center text-gray-700">We couldn't check your forms. Please see the front desk.</p>
        )}
        {dueNotice && <p className="text-sm text-gray-600 text-center">{dueNotice}</p>}
        {menuIsSecondary && packages.length > 0 && (
          <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 pt-4">
            Other forms
          </h3>
        )}
        {packages.map((pkg) => (
          <FormHubRow
            key={pkg.package_key}
            label={pkg.name}
            description={pkg.description || 'Review and complete this form'}
            loading={issuingPackageKey === pkg.package_key}
            disabled={issuingPackageKey !== null}
            onSelect={() => onSelect(pkg.package_key)}
          />
        ))}
        {error && <p className="error-text">{error}</p>}
        <div className="pt-4">
          <button onClick={onDone} className="btn-primary" disabled={issuingPackageKey !== null}>
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

function FormHubRow({
  label,
  description,
  loading = false,
  disabled = false,
  onSelect,
}: {
  label: string;
  description: string;
  loading?: boolean;
  disabled?: boolean;
  onSelect: () => void;
}) {
  return (
    <div className="flex items-center justify-between p-4 rounded-xl border-2 border-gray-200 transition-all">
      <div className="flex items-center gap-3">
        <span className="text-xl">{'⬜'}</span>
        <div>
          <h3 className="text-base font-semibold text-gray-900">{label}</h3>
          <p className="text-sm text-gray-500">{description}</p>
        </div>
      </div>
      <button
        onClick={onSelect}
        disabled={disabled}
        className="px-4 py-2 rounded-lg text-sm font-medium transition-colors text-white bg-blue-600 hover:bg-blue-700"
      >
        {loading ? 'Opening…' : 'Fill Out'}
      </button>
    </div>
  );
}

function KioskIdentifyPanel({ clientId, onSession }: { clientId: string; onSession: (s: KioskV2Session) => void }) {
  const [phone, setPhone] = useState('');
  const [parts, setParts] = useState<BirthdayParts>({ month: '', day: '', year: '' });
  const [dobError, setDobError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const activeError = dobError ?? error;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setDobError(null);
    const digits = phone.replace(/\D/g, '');
    if (digits.length < 10) {
      setError('Please enter a valid 10-digit phone number.');
      return;
    }
    const result = parseBirthday(parts, getLocalToday());
    if (result.status !== 'valid') {
      const messages = {
        empty: '"Date of birth" is required.',
        partial: 'Enter the month, day and year for "Date of birth".',
        invalid: '"Date of birth" must be a real date.',
        future: `"Date of birth" can't be in the future.`,
      };
      setDobError(messages[result.status]);
      return;
    }
    setError('');
    setLoading(true);
    try {
      const session = await kioskCheckIn(clientId, { phone: digits, date_of_birth: result.iso });
      onSession(session);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Verification failed.';
      const status = (err as { status?: number }).status;
      if (status === 401 || status === 404) {
        setError("We couldn't find your record. Please ask the front desk for help.");
      } else if (status === 503) {
        // Forms are unavailable, or kioskCheckIn rejected an unsupported response
        // such as an older backend answering the retired V1 session shape.
        setError("Forms are temporarily unavailable. Please ask the front desk for help.");
      } else {
        setError(message);
      }
    } finally {
      setLoading(false);
    }
  }

  function formatPhone(value: string) {
    const digits = value.replace(/\D/g, '').slice(0, 10);
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }

  return (
    <div>
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Patient Check-In</h2>
        <p className="text-gray-500 mt-1">Verify your identity to get started.</p>
      </div>
      <AnimatePresence mode="wait">
        {loading ? (
          <LoadingState key="loading" type="verification" message="Verifying Identity" />
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="label">Phone number</label>
              <input
                className="input-field"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(formatPhone(e.target.value))}
                placeholder="(555) 123-4567"
                required
              />
            </div>
            <BirthdayField
              field={{ label: 'Date of birth', required: false }}
              inputId="kiosk-dob"
              parts={parts}
              onPartChange={(part, value) => {
                setParts((current) => ({ ...current, [part]: value }));
                setDobError(null);
                setError('');
              }}
              disableAutofill
              errorId={activeError ? 'kiosk-dob-error' : undefined}
              className="input-field text-base"
            />
            {activeError && <p id="kiosk-dob-error" role="alert" className="error-text">{activeError}</p>}
            <button type="submit" className="btn-primary" disabled={loading}>
              Check In
            </button>
          </form>
        )}
      </AnimatePresence>
    </div>
  );
}
